import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, Clock } from 'lucide-react';
import { Badge } from './ui/badge';

function stripContent(text) {
  if (!text) return '';
  // Strip HTML tags first
  let stripped = text.replace(/<[^>]+>/g, ' ');
  // Strip markdown image tags
  stripped = stripped.replace(/\[IMG:[^\]]+\]/g, '');
  // Strip remaining markdown formatting
  stripped = stripped
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^>\s+/gm, '')
    .replace(/^[\-\*]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^(-{3,}|_{3,}|\*{3,})$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped;
}

function readingTime(content) {
  if (!content) return 1;
  const words = stripContent(content).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

export function PostCard({ post, index = 0 }) {
  const formattedDate = new Date(post.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const cleanExcerpt = post.excerpt 
    ? stripContent(post.excerpt) 
    : stripContent(post.content).substring(0, 100) + '...';

  const minutes = readingTime(post.content);
  const categories = post.categories?.length ? post.categories : (post.category ? [post.category] : []);

  const updatedDate = post.updated_at ? new Date(post.updated_at) : null;
  const showUpdated =
    updatedDate &&
    !isNaN(updatedDate) &&
    updatedDate.toDateString() !== new Date(post.created_at).toDateString();
  const formattedUpdated = showUpdated
    ? updatedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

  const delayClass = index < 6 ? `animate-fade-in-delay-${Math.min(index, 3)}` : 'animate-fade-in';

  return (
    <article 
      className={`group border border-border rounded-lg overflow-hidden bg-card hover:-translate-y-1 hover:shadow-lg transition-all duration-200 ${delayClass}`}
      data-testid={`post-card-${post.slug}`}
    >
      {post.featured_image && (
        <Link to={`/post/${post.slug}`}>
          <div className="aspect-video overflow-hidden">
            <img
              src={post.featured_image.startsWith('/api') 
                ? `${process.env.REACT_APP_BACKEND_URL}${post.featured_image}` 
                : post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        </Link>
      )}
      
      <div className="p-6">
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map((cat) => (
              <Badge key={cat} variant="secondary">
                <Tag className="h-3 w-3 mr-1" />
                {cat}
              </Badge>
            ))}
          </div>
        )}
        
        <Link to={`/post/${post.slug}`}>
          <h2 className="text-xl font-semibold tracking-tight mb-2 group-hover:text-primary transition-colors">
            {post.title}
          </h2>
        </Link>
        
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {cleanExcerpt}
        </p>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center">
            <Calendar className="h-3 w-3 mr-1" />
            <time dateTime={post.created_at}>{formattedDate}</time>
          </span>
          <span className="flex items-center" data-testid="reading-time">
            <Clock className="h-3 w-3 mr-1" />
            {minutes} min read
          </span>
          {showUpdated && (
            <span className="flex items-center italic" data-testid="card-updated">
              Updated {formattedUpdated}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
